import React, { useState, useEffect, useCallback } from 'react';
import { duelVotingApi } from '../../api';
import HrnbCard from '../controls/HrnbCard';
import HrnbButton from '../controls/HrnbButton';
import HrnbTextArea from '../controls/HrnbTextArea';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbModal from '../controls/HrnbModal';
import HrnbProgressBar from '../controls/HrnbProgressBar';
import '../../css/components/DuelVoting.css';

const DuelVoting = ({ duelId, round, currentUser, onVoteUpdate }) => {
    const [voteForm, setVoteForm] = useState({ participantId: '', comment: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [hasVoted, setHasVoted] = useState(false);
    const [myVote, setMyVote] = useState(null);

    // Проверка, голосовал ли пользователь
    const checkMyVote = useCallback(async () => {
        if (!currentUser || !round) return;
        // Здесь можно добавить API метод для проверки статуса голосования
        // Пока заглушка
    }, [currentUser, round]);

    useEffect(() => {
        checkMyVote();
    }, [checkMyVote]);

    // Обработка изменения формы
    const handleInputChange = (field, value) => {
        setVoteForm(prev => ({ ...prev, [field]: value }));
    };

    // Отправка голоса
    const handleSubmitVote = async () => {
        if (!voteForm.participantId) {
            setError('Выберите участника для голосования');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            if (hasVoted && myVote) {
                // Обновление голоса
                await duelVotingApi.updateVote(duelId, round.id, {
                    participantId: voteForm.participantId,
                    comment: voteForm.comment
                });
                setSuccess('Голос обновлён');
            } else {
                // Новый голос
                await duelVotingApi.voteForTrack(duelId, round.id, {
                    participantId: voteForm.participantId,
                    comment: voteForm.comment
                });
                setSuccess('Голос успешно отправлен');
                setHasVoted(true);
            }

            setVoteForm({ participantId: '', comment: '' });
            if (onVoteUpdate) {
                onVoteUpdate();
            }
        } catch (err) {
            console.error('[DuelVoting] Failed to submit vote:', err.message);
            setError(err.response?.data?.message || 'Не удалось отправить голос');
        } finally {
            setLoading(false);
        }
    };

    // Удаление голоса
    const handleDeleteVote = async () => {
        if (!window.confirm('Вы уверены, что хотите удалить свой голос?')) return;

        setLoading(true);
        setError(null);

        try {
            await duelVotingApi.deleteVote(duelId, round.id);
            setSuccess('Голос удалён');
            setHasVoted(false);
            setMyVote(null);
            if (onVoteUpdate) {
                onVoteUpdate();
            }
        } catch (err) {
            console.error('[DuelVoting] Failed to delete vote:', err.message);
            setError(err.response?.data?.message || 'Не удалось удалить голос');
        } finally {
            setLoading(false);
        }
    };

    return (
        <HrnbCard title={`Голосование: Раунд ${round?.number || 1}`} className="duel-voting-card">
            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)} className="mb-3">
                    {error}
                </HrnbAlert>
            )}

            {success && (
                <HrnbAlert variant="success" onClose={() => setSuccess(null)} className="mb-3">
                    {success}
                </HrnbAlert>
            )}

            {!currentUser ? (
                <div className="duel-voting-auth-required">
                    <span className="duel-voting-auth-icon">🔐</span>
                    <p>Войдите, чтобы проголосовать</p>
                </div>
            ) : hasVoted ? (
                <div className="duel-voting-voted">
                    <div className="duel-voting-voted-icon">✅</div>
                    <h4>Вы уже проголосовали</h4>
                    <p>Вы можете изменить свой голос или удалить его</p>
                    <div className="duel-voting-voted-actions">
                        <HrnbButton
                            variant="outline-primary"
                            onClick={() => setHasVoted(false)}
                            disabled={loading}
                        >
                            Изменить голос
                        </HrnbButton>
                        <HrnbButton
                            variant="outline-danger"
                            onClick={handleDeleteVote}
                            disabled={loading}
                        >
                            Удалить голос
                        </HrnbButton>
                    </div>
                </div>
            ) : (
                <div className="duel-voting-form">
                    <div className="duel-voting-participant-select">
                        <label className="duel-voting-label">
                            Выберите участника *
                        </label>
                        <div className="duel-voting-participants-grid">
                            {round?.participants?.map(participant => (
                                <div
                                    key={participant.id}
                                    className={`duel-voting-participant-card ${voteForm.participantId === participant.id ? 'selected' : ''}`}
                                    onClick={() => handleInputChange('participantId', participant.id)}
                                >
                                    <div className="duel-voting-participant-avatar">
                                        {participant.avatar ? (
                                            <img src={participant.avatar} alt={participant.displayName} />
                                        ) : (
                                            <span>{participant.displayName?.[0]?.toUpperCase() || '?'}</span>
                                        )}
                                    </div>
                                    <div className="duel-voting-participant-name">
                                        {participant.displayName}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="duel-voting-comment">
                        <label className="duel-voting-label">
                            Комментарий (опционально)
                        </label>
                        <HrnbTextArea
                            value={voteForm.comment}
                            onChange={(e) => handleInputChange('comment', e.target.value)}
                            placeholder="Ваш комментарий к выступлению..."
                            rows={4}
                        />
                    </div>

                    <HrnbButton
                        variant="primary"
                        onClick={handleSubmitVote}
                        disabled={!voteForm.participantId || loading}
                        className="duel-voting-submit"
                    >
                        {loading ? <HrnbLoader size="sm" /> : 'Отправить голос'}
                    </HrnbButton>
                </div>
            )}
        </HrnbCard>
    );
};

export default DuelVoting;
